import { equals, copy } from "angular";
import { v4 as uuidv4 } from "uuid";
import goalIconUrl from "wwwroot/img/icons/goal.svg";
import metricIconUrl from "wwwroot/img/icons/metric.svg";
import { Group, KonvaEventObject, Shape as KonvaShape, ShapeConfig as KonvaShapeConfig } from "konva/lib/Core";
import { Rect } from "konva/lib/shapes/Rect";
import { Text } from "konva/lib/shapes/Text";
import { Circle } from "konva/lib/shapes/Circle";
import { Collection } from "konva/lib/Util";
import { nodeSize, addImageToGroup, ISize, IShapeReactionState } from "./util";
import { inlineEdit } from "../behaviors/inline-edit";
import { fireStateEvent } from "../events";
import { IProgress } from "@gtmhub/core";
import { ITag } from "@gtmhub/tags/models";
import { clipSquare, nucleoOutlineIconText } from "../util";
import { linkable, LINK_DRAGLEAVE, LINK_DRAGOVER, LINK_DROP, LinkingKonvaEventObject, setLinkingEnabled, allowLinkingTo } from "../behaviors/linkable";
import { IGoalParent } from "@gtmhub/goals/models";
import { IWhiteboardRendererContext } from "../whiteboard-renderer";
import { WhiteboardShapeStateType, IWhiteboardShapeState } from ".";
import { IShapeState, WhiteboardShape } from "./shape";
import { createDraftMetric } from "../state/shape-state-converter";
import { IUnitFormatting } from "@gtmhub/shared/models";
import { splitArrayIntoTwo } from "@gtmhub/shared/components";
import { showUsersListTooltip, hideUsersListTooltip } from "../behaviors/users-list-tooltip";
import { localize } from "@gtmhub/localization";
import { CfMap } from "@gtmhub/customFields";
import { voting } from "../behaviors/voting";
import { Stage, Layer } from "konva/lib/Core";
import { unattachOrdereableEvents } from "../behaviors/ordereable/ordereable";
import { attachOrdereableEventsV2, isDummyMetric } from "../behaviors/ordereable";
import { Image as KonvaImage } from "konva/lib/shapes/Image";
import { SHAPE_SELECTED } from "../behaviors/shape-context-menu";
import { AttainmentTypeString } from "@webapp/okrs/goals/models/goal.models";
import { MetricTargetOperator } from "@webapp/okrs/metrics/models/metric.models";

const textColors = {
  goal: "#384758",
  kr: "#51617A",
  addKr: "#1579e0",
};

export const cloneAsImportedGoal = (draftGoal: IDraftGoalState, goal: { id: string; metrics: Record<string, string> }): IImportedGoalState => {
  const importedGoal = copy(draftGoal) as unknown as IImportedGoalState;
  importedGoal.type = "goal";
  importedGoal.id = goal.id;

  for (let i = 0; i < importedGoal.metrics.length; i++) {
    const draftMetric = importedGoal.metrics[i];
    const createdMetricId = goal.metrics[draftMetric.id];
    if (!createdMetricId) {
      throw new Error(`Cannot convert draft to imported goal: cannot find created metric ID for ${draftMetric.id} (created goal is ${goal.id})`);
    }

    draftMetric.id = createdMetricId;
  }

  return importedGoal;
};

export const cloneGoal = (goal: IGoalState): IDraftGoalState => {
  const draftGoal = copy(goal) as unknown as IDraftGoalState;
  draftGoal.type = "draftGoal"; // always clone as draft
  draftGoal.id = uuidv4();

  for (const metric of draftGoal.metrics) {
    metric.id = uuidv4();
  }

  delete draftGoal.parent;
  delete draftGoal.bsonId;
  delete draftGoal.reactions;

  return draftGoal;
};

const attainmentPercentage = (value: number): string => `${Math.round(value * 100)}%`;

export const sortMetricsByOrderId = (metrics: (IImportedGoalMetricState | IDraftGoalMetricState)[]): (IImportedGoalMetricState | IDraftGoalMetricState)[] => {
  return metrics.sort((a, b) => a.orderId - b.orderId);
};

export const rowHeight = (row: Row) => Math.max(nodeSize(row).height, row.findOne(".row-name").height());

export interface IDraftGoalMetricState {
  id: string;
  name: string;
  ownerId?: string;
  ownerIds?: string[];
  targetOperator: MetricTargetOperator;
  target: number;
  manualType: string;
  initialValue: number;
  description: string;
  richTextDescription?: string;
  format: IUnitFormatting;
  dueDate: string;
  softDueDate: string;
  customFields?: CfMap;
  richTextCustomFields?: CfMap;
  orderId: number;
}

export interface IImportedGoalMetricState extends IDraftGoalMetricState {
  attainment: IProgress;
}

export interface IGoalStateBase extends IShapeState {
  name: string;
  ownerId?: string;
  ownerIds?: string[];
  sessionId?: string;
  tags?: ITag[];
  parent?: IGoalParent;
  attainmentTypeString: AttainmentTypeString;
  description?: string;
  richTextDescription?: string;
  private?: boolean;
  customFields?: CfMap;
  richTextCustomFields?: CfMap;
}

export interface IImportedGoalState extends IGoalStateBase {
  type: "goal";
  attainment: IProgress;
  metrics: IImportedGoalMetricState[];
}

export interface IDraftGoalState extends IGoalStateBase, IShapeReactionState {
  type: "draftGoal";
  metrics: IDraftGoalMetricState[];
}

export type IGoalState = IImportedGoalState | IDraftGoalState;

export const isDraftOrImportedGoalState = (state: IWhiteboardShapeState): state is IGoalState => state.type === "goal" || state.type === "draftGoal";

const OWNERS_PREVIEW_NUMBER = 3;
const ICON_SIZE = 16;
const TAG_Y = 25;
const PADDING_AROUND_CONTENTS = 10;
const MULTIPLE_OWNERS_PADDING = ICON_SIZE - 5;
const PADDING_BETWEEN_ICONS = 5;
const ATTAINMENT_WIDTH = 60;
export const isImported = (state: IGoalState): state is IImportedGoalState => state.type === "goal";
const padWidth = (state: IGoalState) => (isImported(state) ? 290 + ATTAINMENT_WIDTH : 290);

export class Goal extends WhiteboardShape<IGoalState> {
  private padNode: Rect;

  private goalNamePlaceholder: string;
  private createDummyMetric: () => IDraftGoalMetricState;
  private clickOnMetricIndex = -1;
  private metricRowsCollection: Collection<Row>;

  public stage: Stage;
  public layer: Layer;
  public metricNamePlaceholder: string;
  public context: IWhiteboardRendererContext;

  static stateTypes: WhiteboardShapeStateType[] = ["goal", "draftGoal"];

  constructor(state: IGoalState, context: IWhiteboardRendererContext, stage: Stage, layer: Layer) {
    super(state, context);
    this.stage = stage;
    this.layer = layer;
    this.context = context;

    this.goalNamePlaceholder = `${localize("type_your_objective")}...`;
    this.metricNamePlaceholder = `+ ${localize("add_metric")}`;
    this.createDummyMetric = (): IDraftGoalMetricState => createDraftMetric({ name: "", ownerIds: state.ownerIds });

    this.pad();
    if (!isImported(state)) {
      this.tag();
    }

    this.createRows(state);
    this.resize();

    if (!isImported(state)) {
      linkable(this);
      setLinkingEnabled(this, !state.parent);
    }
    allowLinkingTo(this);

    this.attachDragEvents();

    if (this.state.type === "draftGoal" && this.context.whiteboardVotingEnabled) {
      voting(this as WhiteboardShape<IDraftGoalState>, this.context);
    }
  }

  updateState(state: IGoalState): boolean {
    let updated = false;
    if (!equals(this.state.position, state.position)) {
      this.position(state.position);
      updated = true;
    }

    if (!equals(this.state.name, state.name)) {
      this.findOne<Row>(".goal-row").rowName(state.name);
      this.resize();
      updated = true;
    }

    //  when the shape is async it doesn't have an ownerIds value
    if (state.ownerIds && !equals(this.state.ownerIds, state.ownerIds)) {
      this.findOne<Row>(".goal-row").owners(state.ownerIds);
      updated = true;
    }

    if (isImported(state) && isImported(this.state) && !equals(this.state.attainment, state.attainment)) {
      this.findOne<Row>(".goal-row").attainment(state.attainment);
      updated = true;
    }

    const hasSetName = !this.state.name && !!state.name;
    if (!equals(this.state.metrics, state.metrics) || hasSetName) {
      this.metrics(state.metrics, !!state.name);
      this.resize();
      updated = true;
    }

    if (!isImported(this.state)) {
      setLinkingEnabled(this, !state.parent);
    }

    this.state = state;
    this.clickOnMetricIndex = -1;
    return updated;
  }

  resize(): void {
    this.positionRows();

    let { height } = this.innerContentsSize();
    if (!isImported(this.state)) {
      height -= this.padNode.y();
    }

    const currentHeight = this.padNode.height();
    const newHeight = Math.max(height + PADDING_AROUND_CONTENTS, 47);
    if (currentHeight !== newHeight) {
      this.padNode.height(newHeight);
      this.fire("resize");
    }
  }

  goalRow(): Row {
    return this.findOne<Row>(".goal-row");
  }

  metricRows(): Collection<Row> {
    return this.find<Row>(".metric-row");
  }

  metricRow(metricId: string): Row {
    return this.findOne<Row>(`#metric-row-${metricId}`);
  }

  private positionRows() {
    const goalRow = this.goalRow();
    goalRow.y(PADDING_AROUND_CONTENTS + (!isImported(this.state) ? TAG_Y : 0));

    // the name node might be invisible while re-calculating the row size,
    // so we need to manually take it into consideration

    let top = goalRow.y() + rowHeight(goalRow) + 20;
    this.metricRows().each((row) => {
      row.y(top);
      top += rowHeight(row) + 15;
    });
  }

  private createRows(state: IGoalState) {
    const goalRow = new Row(
      {
        parent: this,
        iconUrl: goalIconUrl,
        placeholder: this.goalNamePlaceholder,
        isTitle: true,
        inlineEdit: isImported(state)
          ? undefined
          : (evt, reason, value) => {
              if (reason === "enter" && !state.metrics.length) {
                this.clickOnMetricIndex = 0;
              }

              if (reason === "enter" || reason === "outsideClick") {
                value = value.trim();
                if (value === this.goalNamePlaceholder) {
                  value = "";
                }

                if (value !== this.state.name) {
                  fireStateEvent("stateChange", evt, this, { state: this.state, changes: { name: value.trim() } });
                }
              }
            },
      },
      {
        ownerIds: state.ownerIds,
        rowName: state.name,
        attainment: isImported(state) ? state.attainment : undefined,
      },
      this.context
    )
      .addName("goal-row row")
      .allowLinking(true);
    this.add(goalRow);

    this.metrics(state.metrics, !!state.name);
  }

  public metrics(metrics: (IDraftGoalMetricState | IImportedGoalMetricState)[], hasGoalName: boolean) {
    const allMetrics = [...metrics];
    this.metricRowsCollection = this.metricRows();
    const deletedMetricRowCount = 1;
    const dummyMetricRowCount = 1;
    const metricRowCountIncludingDeletedAndDummyRows = allMetrics.length + deletedMetricRowCount + dummyMetricRowCount;
    const metricRowsContainDeletedAndDummyMetric = metricRowCountIncludingDeletedAndDummyRows === this.metricRowsCollection.length;

    if (metricRowsContainDeletedAndDummyMetric) {
      const dummyMetricRow = this.metricRowsCollection[this.metricRowsCollection.length - 1];
      dummyMetricRow.destroy();
      this.metricRowsCollection = this.metricRows();
    }

    if (!isImported(this.state) && ((!allMetrics.length && hasGoalName) || (allMetrics.length && !!allMetrics[allMetrics.length - 1].name))) {
      allMetrics.push(this.createDummyMetric());
    }

    for (let i = 0; i < allMetrics.length; i++) {
      const metric = allMetrics[i];
      const attainment = isImported(this.state) ? (metric as IImportedGoalMetricState).attainment : undefined;

      if (i === this.metricRowsCollection.length) {
        const metricRow = new Row(
          {
            parent: this,
            iconUrl: metricIconUrl,
            placeholder: this.metricNamePlaceholder,
            inlineEdit: isImported(this.state)
              ? undefined
              : (evt, reason, value) => {
                  if (reason === "backspace") {
                    const dummyMetricRow = this.metricRowsCollection[this.metricRowsCollection.length - 1];
                    if (dummyMetricRow !== metricRow) {
                      metricRow.destroy();
                    }

                    const metrics = this.state.metrics.filter((m) => m.id !== metric.id);
                    fireStateEvent("stateChange", evt, this, { state: this.state, changes: { metrics } });
                    return;
                  }

                  if (value && (reason === "enter" || reason === "outsideClick")) {
                    value = value.trim();
                    if (value === this.metricNamePlaceholder) {
                      value = "";
                    }

                    const metrics = [...this.state.metrics];
                    const updatedMetric: IDraftGoalMetricState = { ...metric, name: value };
                    let index = metrics.findIndex((m) => m.id === metric.id);

                    if (reason === "enter") {
                      // Always focus next row/next metric on Enter
                      if (index < 0) {
                        this.clickOnMetricIndex = metrics.length + 1;
                      } else {
                        this.clickOnMetricIndex = index + 1;

                        if (metrics[index].name === value) {
                          this.focusNextMetric();
                          return;
                        }
                      }
                    }

                    if (index < 0) {
                      metrics.push(updatedMetric);
                      index = metrics.length - 1;

                      fireStateEvent("stateChange", evt, this, { state: this.state, changes: { metrics } });
                      this.fire(SHAPE_SELECTED);
                    } else {
                      if (metrics[index].name !== value) {
                        metrics[index] = updatedMetric;

                        fireStateEvent("stateChange", evt, this, { state: this.state, changes: { metrics } });
                      }
                    }
                  }
                },
          },
          {
            ownerIds: metric.ownerIds,
            ownerId: metric.ownerId,
            rowName: metric.name,
            attainment,
          },
          this.context
        )
          .addName("metric-row row")
          .id(`metric-row-${metric.id}`);
        this.metricRowsCollection.push(metricRow);
        this.add(metricRow);
      } else {
        this.metricRowsCollection[i].rowName(metric.name).assign(metric).attainment(attainment);
      }

      if (!isDummyMetric(metric)) {
        unattachOrdereableEvents(this.metricRowsCollection[i]);
        attachOrdereableEventsV2(this.metricRowsCollection[i], this, this.stage, this.layer);
      }

      this.metricRowsCollection[i].allowLinking(!!metric.name);
    }

    this.focusNextMetric();
  }

  private focusNextMetric() {
    if (!isImported(this.state) && this.clickOnMetricIndex >= 0 && this.clickOnMetricIndex < this.metricRowsCollection.length) {
      const index = this.clickOnMetricIndex;
      requestAnimationFrame(() => {
        this.metricRowsCollection[index].clickRowName();
      });
    }
  }

  private pad() {
    this.padNode = new Rect({
      width: padWidth(this.state),
      height: 47,
      fill: "#ffffff",
      stroke: "#e0e4ea",
      strokeWidth: 1,
      cornerRadius: 5,
      dashEnabled: !isImported(this.state),
      x: 0,
      y: !isImported(this.state) ? TAG_Y : 0,
    });
    this.add(this.padNode);
  }

  private tag() {
    const padding = 5;

    const tagNode = new Group({ name: "tag" });
    const text = new Text({ x: padding, y: 6, text: `${localize("draft").toUpperCase()}`, fontSize: 9, fill: "#7989A0", fontStyle: "bold" });
    const background = new Rect({
      width: text.width() + padding * 2,
      height: 20,
      fill: "#E9F0F9",
      stroke: "#D7E4EF",
      strokeWidth: 1,
      cornerRadius: 4,
    });
    tagNode.add(background, text);
    this.add(tagNode);
  }

  private innerContentsSize(): ISize {
    // size of the shape without its tag and other external elements
    return nodeSize(this, (n) => n.hasName("inner-contents"));
  }

  private attachDragEvents() {
    this.on(LINK_DRAGOVER, (evt: LinkingKonvaEventObject) => {
      if (evt.shape === this) {
        return;
      }

      this.find<Row>(".row").each((row) => row.hideDropPad());

      const row = evt.target.findAncestor(".row", true, this) as Row;
      if (row && row.allowLinking()) {
        row.showDropPad();
      }

      this.draw();
    });

    this.on(LINK_DRAGLEAVE, (evt: LinkingKonvaEventObject) => {
      if (evt.shape === this) {
        return;
      }

      this.find<Row>(".row").each((row) => row.hideDropPad());
      this.draw();
    });

    this.on(LINK_DROP, (evt: LinkingKonvaEventObject) => {
      if (evt.shape === this) {
        return;
      }

      const rows = this.find<Row>(".row");
      rows.each((row) => row.hideDropPad());

      const row = evt.target.findAncestor(".row", true, this) as Row;
      if (row && row.allowLinking()) {
        rows.each((r, index) => {
          if (r !== row) {
            return;
          }

          let metricId: string;
          if (index > 0) {
            metricId = this.state.metrics[index - 1].id;
          }

          const parent: IGoalParent = {
            type: metricId ? "metric" : "goal",
            id: metricId || this.state.id,
          };
          const changes: Record<string, unknown> = { parent };
          const sessionId = (evt.shape.state as IGoalState).sessionId || this.state.sessionId;
          if (sessionId !== this.state.sessionId) {
            changes.sessionId = sessionId;
          }

          fireStateEvent("stateChange", evt, evt.shape, { state: evt.shape.state, changes });
        });
      }

      this.draw();
    });
  }
}

interface RowState {
  rowName: string;
  ownerId?: string;
  ownerIds?: string[];
  attainment?: IProgress;
}

type RowUpdateReason = "enter" | "outsideClick" | "backspace";

export class Row extends Group {
  private placeholder: string;
  private hasAttainment: boolean;
  private linkingAllowed: boolean;
  private icon: KonvaImage;

  constructor(
    private options: {
      parent: Goal;
      iconUrl: string;
      placeholder: string;
      isTitle?: boolean;
      inlineEdit?(evt: KonvaEventObject<MouseEvent>, reason: RowUpdateReason, value: string): void;
    },
    state: RowState,
    private context: IWhiteboardRendererContext
  ) {
    super({ name: "inner-contents" });

    this.placeholder = options.placeholder;
    this.hasAttainment = !!state.attainment;

    this.rowName(state.rowName);
    this.dropPad();
    this.attainment(state.attainment);
    this.assign(state);
    this.setIcon(options.iconUrl);
  }

  allowLinking(): boolean;
  allowLinking(value: boolean): this;
  allowLinking(value?: boolean): boolean | this {
    if (typeof value === "boolean") {
      this.linkingAllowed = value;
      return this;
    } else {
      return this.linkingAllowed;
    }
  }

  public showDropPad() {
    this.findOne(".drop-pad").show();
  }

  hideDropPad() {
    this.findOne(".drop-pad").hide();
  }

  clickRowName() {
    this.findOne(".row-name").fire("click");
  }

  rowName(name: string): this {
    let nameNode = this.findOne<Text>(".row-name");
    if (!nameNode) {
      const x = 2 * ICON_SIZE;

      const OFFSET_FROM_END = 15; // looks better with it
      const OWNERS_WIDTH = ICON_SIZE + MULTIPLE_OWNERS_PADDING * (OWNERS_PREVIEW_NUMBER - 1);
      const ITEM_ICON = ICON_SIZE;

      const width =
        padWidth(this.options.parent.state) -
        ITEM_ICON -
        OWNERS_WIDTH -
        PADDING_AROUND_CONTENTS * 2 -
        PADDING_BETWEEN_ICONS * 3 -
        (this.hasAttainment ? ATTAINMENT_WIDTH : OFFSET_FROM_END);

      nameNode = new Text({
        name: "row-name inner-contents",
        x,
        y: -1,
        fontSize: 14,
        lineHeight: 1.4,
        width,
      });
      this.add(nameNode);

      if (this.options.inlineEdit) {
        inlineEdit(this.options.parent, nameNode, {
          multiline: true,
          autoGrow: true,
          enterSubmits: true,
          textColor: this.options.isTitle ? textColors.goal : textColors.kr,
          destroyOnBackspace: !this.options.isTitle,
          placeholder: this.placeholder,
          onUpdate: (evt, reason, value) => {
            nameNode.text(value || this.placeholder);

            if (reason === "enter" || reason === "outsideClick" || reason === "backspace") {
              const myEvt: KonvaEventObject<MouseEvent> = { ...evt, target: this as KonvaShape<KonvaShapeConfig> };
              this.options.inlineEdit(myEvt, reason, value);
            }
          },
        });
      }
    }

    const isImportedGoal = isImported(this.options.parent.state);

    nameNode.setAttrs({
      text: !isImportedGoal ? name || this.placeholder : name,
      fontStyle: name || isImportedGoal ? (this.options.isTitle ? "bold" : "normal") : "normal",
      fill: this.options.isTitle ? textColors.goal : name ? textColors.kr : textColors.addKr,
    });
    return this;
  }

  avatar(id: string, x: number): void {
    const owner = this.context.assignees(id);
    const radius = 10;

    let opacity: number;
    if (owner) {
      if (owner.isActive === false) {
        opacity = 0.4;
      }
      // this pad serves as:
      // - a placeholder while the picture is being loaded
      // - a background for Nucleo Outline icons
      this.add(
        new Rect({
          name: "owner inner-contents",
          width: ICON_SIZE,
          height: ICON_SIZE,
          fill: owner.color || "#f3f4f9",
          cornerRadius: radius,
          x,
        })
      );
    }

    if (owner && owner.picture) {
      const pictureGroup = new Group({
        name: "owner inner-contents",
        clipFunc: (ctx) => clipSquare(ctx, { x: 0, y: 0, width: ICON_SIZE, height: ICON_SIZE, radius: radius }),
        opacity,
        x,
      });
      addImageToGroup(pictureGroup, owner.picture, { width: ICON_SIZE, height: ICON_SIZE });
      this.add(pictureGroup);
    } else {
      this.add(
        new Text({
          name: "owner inner-contents",
          x,
          width: ICON_SIZE,
          height: ICON_SIZE,
          fontSize: 14,
          fontFamily: "Nucleo Outline",
          fill: owner ? "#fff" : this.findOne<Text>(".row-name").fill(),
          text: nucleoOutlineIconText(owner ? owner.avatar : "emoticons_big-eyes"),
          opacity,
        })
      );
    }
  }

  // Radi Zhivkov: Can be reverted to this.owners(state.ownerIds || [state.ownerId]) in case create-ownerIds-property-for-draft-goals patch doesn't work in backend
  assign(state: IGoalState | IDraftGoalMetricState | IImportedGoalMetricState | RowState): this {
    return this.owners(state.ownerIds);
  }

  owners(ownerIds: string[]): this {
    Array.from(this.find(".owner")).forEach((child) => child.destroy());

    const previewOwnersPadWidth = (ownerIds.length > OWNERS_PREVIEW_NUMBER ? OWNERS_PREVIEW_NUMBER : ownerIds.length) * MULTIPLE_OWNERS_PADDING;

    const { firstPart: previewOwners, secondPart: additionalOwners } = splitArrayIntoTwo(ownerIds, OWNERS_PREVIEW_NUMBER);

    const ownersIconRightPaddingWithAttainment = 60;
    const ownersIconRightPadding = 15;
    let ownersIconWrapper = padWidth(this.options.parent.state) - PADDING_AROUND_CONTENTS - previewOwnersPadWidth - ownersIconRightPadding;
    if (this.hasAttainment) {
      ownersIconWrapper -= ownersIconRightPaddingWithAttainment;
    }

    for (let i = 0; i < previewOwners.length; i++) {
      const x = ownersIconWrapper + MULTIPLE_OWNERS_PADDING * (i + 1);
      this.avatar(previewOwners[i], x);
    }

    if (additionalOwners.length) {
      const x = ownersIconWrapper + MULTIPLE_OWNERS_PADDING * (previewOwners.length + 1);
      this.additionalOwners(additionalOwners, x);
    }

    return this;
  }

  additionalOwners(additionalOwners: string[], x: number): void {
    const additionalOwnersNode = new Group({ name: "additional-owners-btn" });

    const background = new Rect({
      name: "additional inner-contents",
      width: ICON_SIZE,
      height: ICON_SIZE,
      fill: "#8393AE",
      cornerRadius: 10,
      x,
    });

    const text = new Text({
      x: background.x(),
      height: background.height(),
      width: background.width(),
      align: "center",
      verticalAlign: "middle",
      text: `+${additionalOwners.length}`,
      fontSize: 10,
      fill: "white",
      fontStyle: "bold",
    });

    additionalOwnersNode.add(background, text);

    additionalOwnersNode.on("mouseenter", (e) => {
      this.context.hoverAssigneeIds(additionalOwners);
      const { x, y } = e.target.getAbsolutePosition();
      const yWithPadding = y + TAG_Y + PADDING_AROUND_CONTENTS * 2 + ICON_SIZE;

      showUsersListTooltip({ x, y: yWithPadding });
    });

    additionalOwnersNode.on("mouseleave", function () {
      hideUsersListTooltip();
    });

    this.add(additionalOwnersNode);
  }

  attainment(attainment: IProgress): this {
    if (!this.hasAttainment) {
      return this;
    }

    this.find(".attainment").each((node) => node.destroy());

    const topPadding = 3;
    let attainmentNode: KonvaShape<KonvaShapeConfig>;
    if (typeof attainment.value === "number") {
      attainmentNode = new Text({
        name: "attainment inner-contents",
        text: attainmentPercentage(attainment.value),
        align: "right",
        fontSize: 14,
        lineHeight: 1.4,
        fill: attainment.color,
      });
    } else {
      const radius = 8;
      attainmentNode = new Circle({
        name: "attainment inner-contents",
        radius,
        y: topPadding + radius,
        fill: attainment.color,
      });
    }
    attainmentNode.x(padWidth(this.options.parent.state) - attainmentNode.width() - PADDING_AROUND_CONTENTS);
    this.add(attainmentNode);
    return this;
  }

  private dropPad() {
    const rowNameNode = this.findOne(".row-name");
    const padNode = new Rect({
      name: "drop-pad",
      x: 5,
      y: -5,
      width: padWidth(this.options.parent.state) - 10,
      height: Math.max(rowNameNode.height(), ICON_SIZE) + 10,
      opacity: 0.15,
      fill: "#81ACFF",
      cornerRadius: 4,
      visible: false,
    });
    this.add(padNode);
  }

  public setIcon(source: string, id?: string): KonvaImage {
    /** necessary to redraw a new icon when changing the sources */
    if (this.icon) {
      this.icon.remove();
    }

    this.icon = addImageToGroup(this, source, {
      width: ICON_SIZE,
      height: ICON_SIZE,
      name: "inner-contents",
      x: PADDING_AROUND_CONTENTS,
      id,
    });

    return this.icon;
  }

  public getIcon(): KonvaImage {
    return this.icon;
  }
}
